import React, { useState } from 'react';
import { Heading } from '@components/shared';
import { token } from '@atlaskit/tokens';
import { css } from '@emotion/react';
import { IconButton } from '@atlaskit/button/new';
import ChevronDown from '@atlaskit/icon/glyph/chevron-down';
import ChevronUp from '@atlaskit/icon/glyph/chevron-up';

/**
 * Interface representing a single resource item in the panel
 * @interface ResourceItem
 * @property {string} title - The title of the resource
 * @property {string} slug - Unique identifier for the resource
 */

export interface ResourceItem {
  title: string;
  slug: string;
}

/**
 * Props for the RelatedResourcesPanel component
 * @interface RelatedResourcesPanelProps
 * @property {string} title - The title of the resources panel
 * @property {ResourceItem[]} resources - Array of resource items to be displayed
 * @property {(slug: string) => void} onResourceClick - Callback function when a resource is clicked
 * @property {() => void} [onButtonClick] - Optional callback function when the expand/collapse button is clicked
 */

export interface RelatedResourcesPanelProps {
  title: string;
  resources: ResourceItem[];
  onResourceClick: (slug: string) => void;
  onButtonClick?: () => void;
}

/**
 * RelatedResourcesPanel is a collapsible component that displays a list of related resources.
 * It includes a header with a title and an expand/collapse button, and a list of clickable resources.
 *
 * @component
 * @param {RelatedResourcesPanelProps} props - The component props
 * @returns {React.ReactElement} A collapsible panel with related resources
 *
 * @example
 * <RelatedResourcesPanel
 *   title="Related Resources"
 *   resources={resources}
 *   onResourceClick={handleResourceClick}
 *   onButtonClick={handleButtonClick}
 * />
 */

export const RelatedResourcesPanel: React.FC<RelatedResourcesPanelProps> = ({
  title,
  resources,
  onResourceClick,
  onButtonClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleButtonClick = () => {
    setIsOpen(!isOpen);
    onButtonClick && onButtonClick();
  };

  return (
    <div data-testid="related-resources-panel" css={relatedResourcesPanelStyles} onClick={handleButtonClick}>
      <div data-testid="related-resources-header" css={headerStyles}>
        <Heading level={6}>{title}</Heading>
        <IconButton
          appearance="subtle"
          icon={isOpen ? ChevronUp : ChevronDown}
          label={isOpen ? 'Collapse' : 'Expand'}
        />
      </div>
      {isOpen && (
        <ul data-testid="related-resources-list" css={resourcesListStyles}>
          {resources.map(({ title, slug }) => (
            <li
              key={slug}
              data-testid={`related-resource-item-${slug}`}
              css={resourceStyles}
              onClick={() => onResourceClick(slug)}
            >
              {title}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const relatedResourcesPanelStyles = css({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
  padding: `${token('space.150')} ${token('space.200')}`,
  boxShadow: token('elevation.shadow.overlay'),
  cursor: 'pointer',
});

const headerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: token('color.text'),
});

const resourcesListStyles = css({
  display: 'flex',
  flexDirection: 'column',
  margin: 0,
  paddingLeft: token('space.250'),
  gap: token('space.050'),
  marginTop: token('space.150'),
});

const resourceStyles = css({
  color: token('color.link'),
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  cursor: 'pointer',

  '&:hover': {
    textDecoration: 'underline',
  },
});
